import { useAuthStore } from '@/store/auth-store';
import { useTranslation } from '@/lib/i18n/react';
import { LocalizedError } from '@/lib/i18n/errors';
import { sessionRevision, requireCurrentSession, sessionScope } from '@/features/auth/session-scope';
import * as React from 'react';
import { uploadFileToStorage } from '@/features/inbox/upload-queries';
import {
  mimeToChannelMessageType,
  useSendChannelMessage,
} from '../queries';

/** Files upload sequentially, with a literal caption on the first entry.
 * Once send is attempted, the shared owned outbox preserves the complete
 * message/attachment and owns its retry or uncertainty review. Only failures
 * before send retain a File retry here, avoiding two independent send paths. */

export interface ChannelUploadEntry {
  /** Local-only id — React key + dismiss/retry-handler target. */
  id: string;
  name: string;
  status: 'uploading' | 'sending' | 'error';
  /** Stable failure; readers translate it in the current personal language. */
  error?: unknown;
  revision: number;
  /** The original File — kept across failures so `retry(id)` can re-upload. */
  file: File;
  /** Caption this entry carries (only ever set on the batch's first entry).
   *  Preserved on failure so a retry re-sends it with the file. */
  caption?: string;
}

export interface ChannelFileUpload {
  /** Upload + send. Resolves when the whole batch has settled (sent or errored). */
  uploadFiles: (files: FileList | File[], caption?: string) => Promise<void>;
  /** In-flight + errored entries. Successfully sent files leave the list. */
  pending: ChannelUploadEntry[];
  /** True while any file is uploading/sending — callers should block Send. */
  isUploading: boolean;
  /** Drop an errored entry from the list (dismiss the failure chip). */
  dismiss: (id: string) => void;
  /** Re-run upload + send for an errored entry (same File, same caption). */
  retry: (id: string) => void;
}

export function useChannelFileUpload(channelId: string): ChannelFileUpload {
  useTranslation();
  useAuthStore(sessionScope);
  const sendMessage = useSendChannelMessage();
  const [pending, setPending] = React.useState<ChannelUploadEntry[]>([]);
  const idRef = React.useRef(0);
  // mutateAsync is referentially stable in TanStack Query v5, so the callbacks
  // below only re-bind when the channel changes.
  const { mutateAsync: sendAsync } = sendMessage;

  // Upload + send ONE entry; shared by the initial batch loop and retry().
  // On failure the entry stays in `pending` (status 'error') WITH its File +
  // caption so it can be retried; on success it leaves the list for good.
  const processEntry = React.useCallback(
    async (entry: Pick<ChannelUploadEntry, 'id' | 'name' | 'file' | 'caption' | 'revision'>): Promise<void> => {
      let sending = false;
      try {
        requireCurrentSession(entry.revision);
        const res = await uploadFileToStorage(entry.file);
        requireCurrentSession(entry.revision);
        setPending((prev) =>
          prev.map((p) => (p.id === entry.id ? { ...p, status: 'sending' as const } : p)),
        );
        const mime = res.mime_type ?? entry.file.type ?? '';
        sending = true;
        await sendAsync({
          channelId,
          content: entry.caption ?? '',
          fileUrl: res.url,
          fileMimeType: mime || undefined,
          fileName: res.file_name ?? entry.name,
          fileSize: res.size ?? (entry.file.size || undefined),
          messageType: mimeToChannelMessageType(mime),
        });
        requireCurrentSession(entry.revision);
        // Legacy send resolved; its durable receipt is a separate contract gate.
        setPending((prev) => prev.filter((p) => p.id !== entry.id));
      } catch (err) {
        if (entry.revision !== sessionRevision()) return;
        if(sending){setPending(prev=>prev.filter(p=>p.id!==entry.id));return;}
        setPending((prev) =>
          prev.map((p) =>
            p.id === entry.id
              ? {
                  ...p,
                  status: 'error' as const,
                  error: sending ? new LocalizedError('common', 'upload.send_unconfirmed') : err instanceof LocalizedError ? err : new LocalizedError('common', 'upload.unconfirmed'),
                }
              : p,
          ),
        );
      }
    },
    [channelId, sendAsync],
  );

  const uploadFiles = React.useCallback(
    async (files: FileList | File[], caption = ''): Promise<void> => {
      const list = Array.from(files ?? []);
      if (list.length === 0) return;

      const revision = sessionRevision();
      const trimmedCaption = caption;
      const batch: ChannelUploadEntry[] = list.map((file, idx) => ({
        id: `up-${revision}-${++idRef.current}`,
        revision,
        name: file.name || 'archivo',
        status: 'uploading' as const,
        file,
        // Caption bound to the FIRST entry at creation time — if it fails, the
        // caption fails (and retries) with it; later files never inherit it.
        ...(idx === 0 && trimmedCaption ? { caption: trimmedCaption } : {}),
      }));
      setPending((prev) => [...prev.filter(entry => entry.revision === revision), ...batch]);

      for (const entry of batch) {
        if (revision !== sessionRevision()) return;
        await processEntry(entry);
      }
    },
    [processEntry],
  );

  const retry = React.useCallback(
    (id: string) => {
      const entry = pending.find((p) => p.id === id && p.status === 'error' && p.revision === sessionRevision());
      if (!entry) return;
      setPending((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, status: 'uploading' as const, error: undefined } : p,
        ),
      );
      void processEntry(entry);
    },
    [pending, processEntry],
  );

  const dismiss = React.useCallback((id: string) => {
    setPending((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const ownedPending = pending.filter(entry => entry.revision === sessionRevision());
  const isUploading = ownedPending.some((p) => p.status === 'uploading' || p.status === 'sending');

  return { uploadFiles, pending: ownedPending, isUploading, dismiss, retry };
}
